/**
 * Desktop app frame. Fills a 24" (1920) display and still grows on a 27"
 * so browse pages are not a centered 1400px column. Prose pages keep their
 * own narrower containers.
 */
export const APP_FRAME_MAX_W = '2400px';

/**
 * Horizontal inset for the frame. Header, shell, footer, and full-bleed
 * heroes share this so their edges line up.
 */
export const APP_FRAME_PX = { base: 4, xl: 6 } as const;

/** Deck tiles stay about this wide; extra monitor width adds a column. */
export const DECK_GALLERY_TEMPLATE_COLUMNS =
  'repeat(auto-fill, minmax(min(100%, 320px), 1fr))' as const;

/**
 * Gallery tile size. A sharp legend column, then type on the solid panel.
 * Shared by `DeckCard`, its skeleton, and the empty featured slot.
 */
export const DECK_GALLERY_CARD_H = '160px';
export const DECK_GALLERY_ART_W = { base: '96px', md: '120px' } as const;

/** CSS safe-area inset expressions for installed PWAs and notched devices. */
export const SAFE_AREA_INSET_TOP = 'env(safe-area-inset-top, 0px)';
export const SAFE_AREA_INSET_BOTTOM = 'env(safe-area-inset-bottom, 0px)';
export const SAFE_AREA_INSET_LEFT = 'env(safe-area-inset-left, 0px)';
export const SAFE_AREA_INSET_RIGHT = 'env(safe-area-inset-right, 0px)';

/** Apple HIG / Material minimum primary touch target (logical px). */
export const MIN_TOUCH_TARGET_PX = 44;

/**
 * Chakra `md` and `lg`, in pixels. Tablet chrome (floating pill, header
 * search) is `md` until `lg`. Keep these as pixels: an `em` query moves when
 * the root font size changes, and `62em` is 992px, not Chakra's 1024px `lg`.
 */
export const TABLET_MIN_PX = 768;
export const DESKTOP_MIN_PX = 1024;
export const DESKTOP_MIN_MEDIA = `(min-width: ${DESKTOP_MIN_PX}px)`;
export const TABLET_HEADER_SEARCH_MEDIA =
  `(min-width: ${TABLET_MIN_PX}px) and (max-width: ${DESKTOP_MIN_PX - 1}px)`;
export const BELOW_DESKTOP_MEDIA = `(max-width: ${DESKTOP_MIN_PX - 1}px)`;

/**
 * Fallback height of the sticky site header content band (excludes safe-area).
 * Real mobile height is measured and published to {@link MOBILE_SITE_HEADER_OFFSET_VAR}.
 */
export const MOBILE_SITE_HEADER_HEIGHT_PX = 68;

/**
 * Measured mobile header height (includes safe-area padding), set by `AppHeader`.
 * Prefer {@link MOBILE_SITE_HEADER_SAFE_OFFSET} for sticky `top` values.
 */
export const MOBILE_SITE_HEADER_OFFSET_VAR = '--mobile-site-header-offset';

/** Mobile header offset including the iOS standalone/PWA status-bar inset. */
export const MOBILE_SITE_HEADER_SAFE_OFFSET = `var(${MOBILE_SITE_HEADER_OFFSET_VAR}, calc(${MOBILE_SITE_HEADER_HEIGHT_PX}px + ${SAFE_AREA_INSET_TOP}))`;

/**
 * AppLayout’s mobile top padding (header clearance + 1rem). Heroes that bleed
 * under the fixed header pull up by this amount and grow their min-height by it.
 */
export const MOBILE_SHELL_TOP_PADDING = `calc(${MOBILE_SITE_HEADER_SAFE_OFFSET})`;

/** Negative margin to extend a full-bleed cover underneath the fixed mobile header. */
export const MOBILE_HERO_UNDER_HEADER_MT = `calc(-1 * (${MOBILE_SITE_HEADER_SAFE_OFFSET}))`;

/** Height of the SearchStats sticky bar on mobile. */
export const MOBILE_STATS_BAR_HEIGHT_PX = 48;

/**
 * Stage CSS vars written by `useMobileLargeTitleProgress` (unitless 0–1).
 * Unset when no large title is active — AppHeader falls back to a settled bar.
 */
/** Stage 2: large title tucked under the floating pills. */
export const MOBILE_NAV_TITLE_TUCK_VAR = '--mobile-nav-title-tuck';
/** Stage 3: compact centered nav title. Alias kept as `--mobile-nav-title-progress`. */
export const MOBILE_NAV_COMPACT_TITLE_VAR = '--mobile-nav-compact-title';
/** @deprecated Use {@link MOBILE_NAV_COMPACT_TITLE_VAR}. */
export const MOBILE_NAV_TITLE_PROGRESS_VAR = '--mobile-nav-title-progress';
/** Stage 4: frosted material / progressive blur under the chrome. */
export const MOBILE_NAV_MATERIAL_VAR = '--mobile-nav-material';

/**
 * Hide the scrollbar rail without disabling scroll (wheel / trackpad / touch).
 * Use on bounded deck-builder panes so a classic rail cannot change content width.
 */
export const OVERLAY_SCROLL_CSS = {
  WebkitOverflowScrolling: 'touch',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
} as const;
